<!-- Stripe-style homepage: consistent grid structure across sections -->

<!-- 1. Homepage header section (hero + Top teams hiring now as one block) -->
<section
  class="relative w-full overflow-hidden flex flex-col justify-center py-16 md:py-24 md:pt-[80px] pt-[80px]"
>
  <!-- <app-hero-fluid-bg></app-hero-fluid-bg> -->
  <div class="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:pl-14">
    <!-- Hero: text left, animation right -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-12 items-center">
      <!-- Hero content (left); on mobile below animation -->
      <div class="text-center lg:text-left order-2 lg:order-1">
        <h1 class="text-3xl font-bold text-gray-900 dark:text-white sm:text-3xl md:text-4xl lg:text-5xl lg:leading-[1.1]">
          The dedicated job board for UI/UX, Product, and Graphic Designers
        </h1>
        <p class="mt-4 text-base text-gray-600 dark:text-gray-400 sm:text-lg max-w-2xl lg:max-w-none mx-auto lg:mx-0">
          A career engine built by designers, for the next generation of product builders. Skip the noise of general job boards and use precision matching to find roles that value your craft.
        </p>
        <div class="mt-6 sm:mt-8 flex flex-wrap gap-3 sm:gap-4 justify-center lg:justify-start">
          <a routerLink="/jobs" class="btn-primary btn-lg" (click)="trackHomeClick('home_hero_click', { cta: 'view_jobs' })">View jobs</a>
          <a routerLink="/ai-job-matcher" class="btn-secondary btn-lg" (click)="trackHomeClick('home_hero_click', { cta: 'job_matcher' })"
            >Try our Job Matcher</a
          >
        </div>
      </div>

      <!-- Job Matcher demo animation (right); on mobile above text, scaled down -->
      <div class="flex justify-center lg:justify-end order-2 lg:order-2 md:max-h-[630px] md:pt-[70px] max-h-[420px] lg:pr-[40px]">
        <div class="max-lg:scale-[0.85] max-lg:origin-top">
          <app-job-matcher-animation></app-job-matcher-animation>
        </div>
      </div>
    </div>

    <!-- Top teams hiring now -->
    <div class="mt-24 md:mt-32 hidden">
      <h2
        class="text-center text-base font-normal text-[#4B5563] dark:text-gray-400 mb-8"
      >
        Top teams hiring now
      </h2>
      
      <!-- Company logos -->
      <div class="flex flex-wrap items-center justify-center gap-8 md:gap-12">
        <img 
          *ngFor="let company of featuredCompanies"
          [src]="company.logo_img" 
          [alt]="company.company + ' logo'"
          class="h-[30px] w-auto"
        />
      </div>
    </div>
  </div>
</section>

<!-- 2. Modern Designer toolkit -->
<section
  class="relative w-full overflow-hidden py-16 md:py-24"
>
  <!-- Section grid: vertical lines constrained to content, horizontal lines full width -->
  <div
    class="absolute inset-0 pointer-events-none z-[1]"
    aria-hidden="true"
  >
    <!-- Vertical lines container (constrained to content width) -->
    <div class="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-full max-w-7xl px-4 sm:px-6 md:px-8">
      <!-- Left vertical line -->
      <span
        class="absolute left-0 top-0 bottom-0 border-l border-gray-200 dark:border-gray-800"
      ></span>
      <!-- Right vertical line -->
      <span
        class="absolute right-0 top-0 bottom-0 border-r border-gray-200 dark:border-gray-800"
      ></span>
    </div>
    <!-- Horizontal lines: top and bottom (Toolkit is first section with grid, so top line visible) -->
    <span
      class="absolute left-0 right-0 w-full top-0 border-b border-gray-200 dark:border-gray-800 mix-blend-multiply"
    ></span>
    <span
      class="absolute left-0 right-0 w-full bottom-0 border-b border-gray-200 dark:border-gray-800 mix-blend-multiply"
    ></span>
  </div>

  <div class="relative z-10 toolkit-section">
    <!-- Header content in constrained container -->
    <div class="w-full max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
      <span class="inline-block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 px-3 py-1 bg-gray-100 dark:bg-gray-800 rounded-full">TOOLKIT</span>
      <div
        class="grid grid-cols-2 max-md:grid-cols-1 gap-3 md:gap-8 items-start mb-12 md:mb-20"
      >
        <h2 class="text-2xl font-bold text-gray-900 dark:text-white sm:text-2xl md:text-3xl lg:text-4xl tracking-tight leading-tight">
          the Modern Designer toolkit
        </h2>
        <p class="text-base sm:text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
          We've automated the "search" so you can focus on the "craft." Use our
          suite of AI-driven tools to bypass the noise and land your next lead
          role.
        </p>
      </div>
    </div>

    <!-- Cards scroll area: starts at container edge, overflows to viewport edge -->
    <div
      #toolkitCardsScroll
      class="toolkit-cards-scroll"
      role="region"
      aria-label="Toolkit feature cards"
    >
      <div #toolkitCardsGrid class="toolkit-cards-grid">
        <!-- CARD 1: AI Job Matcher -->
        <a routerLink="/ai-job-matcher" (click)="trackHomeClick('home_toolkit_card_clicked', { card: 'job_matcher' })" class="card card-matcher glow-orange bg-[#F2F2F2] dark-gradient-bg border-[1.5px] border-gray-200 dark:border-gray-800 hover:shadow-lg transition-shadow duration-300 ease-out hover:border-gray-300 dark:hover:border-gray-600" aria-label="Open AI Job matcher">
          <div class="card-header">
            <div class="card-header-text">
              <h2 class="text-lg md:text-xl font-bold text-gray-900 dark:text-white leading-tight">Job matcher</h2>
              <p class="mt-1.5 text-sm md:text-base text-gray-500 dark:text-gray-400 leading-snug">Upload your resume and let AI find roles that match your skills, experience, and career goals</p>
            </div>
            <span class="ext-link" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path class="arrow-icon" d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25" />
              </svg>
            </span>
          </div>
          <div class="card-glow-wrapper">
            <span class="card-glow" aria-hidden="true"></span>
          </div>
          <div class="job-card-shadow" aria-hidden="true"></div>
          <div class="viewport">
            <div class="job-card">
              <div class="job-card-top">
                <div class="company">
                  <div class="company-logo">
                    <svg viewBox="0 0 100 100" fill="none">
                      <rect x="10" y="10" width="80" height="80" rx="8" stroke="#111" stroke-width="5" />
                      <path d="M35 75L50 25L65 75" stroke="#111" stroke-width="6" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M62 25L75 75" stroke="#111" stroke-width="6" stroke-linecap="round" />
                    </svg>
                  </div>
                  <span class="company-name">Antrophic</span>
                </div>
                <span class="match-badge">81% Match</span>
              </div>
              <div class="job-title">Senior Product Designer</div>
              <div class="jobloc"><span>🇺🇸</span> San Francisco, USA</div>
              <div class="tags">
                <span class="tag remote">Remote</span>
                <span class="tag">Full-Time</span>
                <span class="tag">Lead</span>
              </div>
              <div class="desc">
                <span class="desc-static">Design the user experience for Claude Code across various platforms, including terminal and IDEs. Collaborate with engineering and research teams to iterate quickly on design solutions.</span>
                <span class="desc-typed"><span class="w">Design </span><span class="w">the </span><span class="w">user </span><span class="w">experience </span><span class="w">for </span><span class="w">Claude </span><span class="w">Code </span><span class="w">across </span><span class="w">various </span><span class="w">platforms, </span><span class="w">including </span><span class="w">terminal </span><span class="w">and </span><span class="w">IDEs. </span><span class="w">Collaborate </span><span class="w">with </span><span class="w">engineering </span><span class="w">and </span><span class="w">research </span><span class="w">teams </span><span class="w">to </span><span class="w">iterate </span><span class="w">quickly </span><span class="w">on </span><span class="w">design </span><span class="w">solutions.</span></span>
              </div>
            </div>
          </div>
        </a>

        <!-- CARD 2: Resume Analyser (not linked) -->
        <div class="card card-resume glow-blue bg-[#F2F2F2] dark-gradient-bg border-[1.5px] border-gray-200 dark:border-gray-800 cursor-pointer hover:border-gray-300 dark:hover:border-gray-600" (click)="openResumeAnalyserModal()" role="button" tabindex="0" (keydown.enter)="openResumeAnalyserModal()" (keydown.space)="openResumeAnalyserModal(); $event.preventDefault()" aria-label="Open Resume analyser feature overview">
          <div class="card-header">
            <div class="card-header-text">
              <h2 class="text-lg md:text-xl font-bold text-gray-900 dark:text-white leading-tight">Resume analyser</h2>
              <p class="mt-1.5 text-sm md:text-base text-gray-500 dark:text-gray-400 leading-snug">Get instant feedback on how your resume performs against ATS systems</p>
            </div>
            <span class="ext-link" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path class="arrow-icon" d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25" />
              </svg>
            </span>
          </div>
          <div class="card-glow-wrapper">
            <span class="card-glow" aria-hidden="true"></span>
          </div>
          <div class="viewport overflow-hidden">
            <div class="resume-inner">
              <div class="resume-banner">
                <div class="label">Before you apply</div>
                <h3>Make sure your resume makes it through AI screening</h3>
                <p>Scan your resume before applying</p>
              </div>
              <div class="pdf-section">
                <div class="pdf-icon-wrap">
                  <hugeicons-icon
                    [icon]="icons.pdf"
                    [size]="40"
                    color="currentColor"
                    [strokeWidth]="1.5"
                  ></hugeicons-icon>
                </div>
                <div class="filename">EvanSharp_Resume.pdf</div>
                <div class="sublabel">Your resume</div>
                <button type="button" class="analyze-btn">
                  <span class="analyze-btn-default">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2l2.4 7.2H22l-6 4.8 2.4 7.2L12 16.4l-6.4 4.8L8 14l-6-4.8h7.6z" /></svg>
                    Analyze this resume
                  </span>
                  <span class="analyze-btn-hover">
                    <span class="analyze-btn-spinner">
                      <hugeicons-icon [icon]="icons.loading" [size]="18" color="currentColor" [strokeWidth]="2"></hugeicons-icon>
                    </span>
                    Analysing your resume
                  </span>
                </button>
                <button type="button" class="upload-btn">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5v14M5 12l7-7 7 7" /></svg>
                  Upload different resume
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- CARD 3: Job Alerts -->
        <a routerLink="/job-alerts" (click)="trackHomeClick('home_toolkit_card_clicked', { card: 'job_alerts' })" class="card card-alerts glow-purple bg-[#F2F2F2] dark-gradient-bg border-[1.5px] border-gray-200 dark:border-gray-800 hover:shadow-lg transition-shadow duration-300 ease-out hover:border-gray-300 dark:hover:border-gray-600" aria-label="Open Job alerts" target="_blank" rel="noopener noreferrer">
          <div class="card-header">
            <div class="card-header-text">
              <h2 class="text-lg md:text-xl font-bold text-gray-900 dark:text-white leading-tight">Job alerts</h2>
              <p class="mt-1.5 text-sm md:text-base text-gray-500 dark:text-gray-400 leading-snug">Set your criteria once and get notified when relevant roles drop. Never miss an opportunity that fits</p>
            </div>
            <span class="ext-link" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path class="arrow-icon" d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25" />
              </svg>
            </span>
          </div>
          <div class="card-glow-wrapper">
            <span class="card-glow" aria-hidden="true"></span>
          </div>
          <div class="viewport">
            <div class="alerts-inner">
              <div class="alerts-top">
                <h3>Frequency</h3>
              </div>
              <div class="freq-options">
                <div class="freq-row"><span>Daily</span><div class="toggle animated"></div></div>
                <div class="freq-row"><span>Every 3 days</span><div class="toggle"></div></div>
                <div class="freq-row"><span>Weekly</span><div class="toggle"></div></div>
              </div>
              <div class="location-section">
                <h4>Location</h4>
                <div class="search-input">
                  <hugeicons-icon
                    [icon]="icons.search"
                    [size]="15"
                    color="#8A8A8A"
                    [strokeWidth]="2"
                  ></hugeicons-icon>
                  <span class="search-placeholder">Search by location</span>
                  <span class="search-type-wrap"><span class="search-type-text">San Francisco, CA</span><span class="search-type-cursor"></span></span>
                </div>
              </div>
            </div>
          </div>
          <img src="assets/images/homepage/email-emoji.png" alt="" class="mail-icon" aria-hidden="true" />
        </a>

        <!-- CARD 4: Fresh New Jobs -->
        <a routerLink="/jobs" (click)="trackHomeClick('home_toolkit_card_clicked', { card: 'new_jobs_daily' })" class="card card-fresh glow-green bg-[#F2F2F2] dark-gradient-bg border-[1.5px] border-gray-200 dark:border-gray-800 hover:shadow-lg transition-shadow duration-300 ease-out hover:border-gray-300 dark:hover:border-gray-600" aria-label="Open Fresh new jobs" target="_blank" rel="noopener noreferrer">
          <div class="card-header">
            <div class="card-header-text">
              <h2 class="text-lg md:text-xl font-bold text-gray-900 dark:text-white leading-tight">New jobs daily</h2>
              <p class="mt-1.5 text-sm md:text-base text-gray-500 dark:text-gray-400 leading-snug">Fresh design roles from companies that actually invest in design. Updated every day</p>
            </div>
            <span class="ext-link" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path class="arrow-icon" d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25" />
              </svg>
            </span>
          </div>
          <div class="card-glow-wrapper">
            <span class="card-glow" aria-hidden="true"></span>
          </div>
          <div class="viewport">
            <div class="fresh-col">
              <div class="job-row"><div class="job-row-icon coinbase"><img src="assets/images/homepage/coinbase.png" alt="Coinbase" /></div><div class="job-row-text"><h4>Product Designer</h4><span>Coinbase</span></div></div>
              <div class="job-row"><div class="job-row-icon duolingo"><img src="assets/images/homepage/duolingo.png" alt="Duolingo" /></div><div class="job-row-text"><h4>Staff Product Designer</h4><span>Duolingo</span></div></div>
              <div class="job-row"><div class="job-row-icon figma"><img src="assets/images/homepage/figma.png" alt="Figma" /></div><div class="job-row-text"><h4>Product Designer</h4><span>Figma</span></div></div>
              <div class="job-row"><div class="job-row-icon okta"><img src="assets/images/homepage/okta.png" alt="Okta" /></div><div class="job-row-text"><h4>Graphic Designer</h4><span>Okta</span></div></div>
              <div class="job-row"><div class="job-row-icon stripe"><img src="assets/images/homepage/stripe.png" alt="Stripe" /></div><div class="job-row-text"><h4>Senior Designer</h4><span>Stripe</span></div></div>
              <div class="job-row"><div class="job-row-icon spotify"><img src="assets/images/homepage/spotify.png" alt="Spotify" /></div><div class="job-row-text"><h4>UX Designer</h4><span>Spotify</span></div></div>
              <div class="job-row"><div class="job-row-icon coinbase"><img src="assets/images/homepage/coinbase.png" alt="Coinbase" /></div><div class="job-row-text"><h4>Product Designer</h4><span>Coinbase</span></div></div>
              <div class="job-row"><div class="job-row-icon duolingo"><img src="assets/images/homepage/duolingo.png" alt="Duolingo" /></div><div class="job-row-text"><h4>Staff Product Designer</h4><span>Duolingo</span></div></div>
              <div class="job-row"><div class="job-row-icon figma"><img src="assets/images/homepage/figma.png" alt="Figma" /></div><div class="job-row-text"><h4>Product Designer</h4><span>Figma</span></div></div>
              <div class="job-row"><div class="job-row-icon okta"><img src="assets/images/homepage/okta.png" alt="Okta" /></div><div class="job-row-text"><h4>Graphic Designer</h4><span>Okta</span></div></div>
              <div class="job-row"><div class="job-row-icon stripe"><img src="assets/images/homepage/stripe.png" alt="Stripe" /></div><div class="job-row-text"><h4>Senior Designer</h4><span>Stripe</span></div></div>
              <div class="job-row"><div class="job-row-icon spotify"><img src="assets/images/homepage/spotify.png" alt="Spotify" /></div><div class="job-row-text"><h4>UX Designer</h4><span>Spotify</span></div></div>
            </div>
          </div>
        </a>

        <!-- CARD 5: Custom Profile -->
        <div
          (click)="openProfileCard()"
          role="button"
          tabindex="0"
          (keydown.enter)="openProfileCard()"
          (keydown.space)="openProfileCard(); $event.preventDefault()"
          class="card card-profile glow-violet bg-[#F2F2F2] dark-gradient-bg border-[1.5px] border-gray-200 dark:border-gray-800 hover:shadow-lg transition-shadow duration-300 ease-out hover:border-gray-300 dark:hover:border-gray-600 cursor-pointer"
          aria-label="Open your profile"
        >
          <div class="card-header">
            <div class="card-header-text">
              <h2 class="text-lg md:text-xl font-bold text-gray-900 dark:text-white leading-tight">Custom profile</h2>
              <p class="mt-1.5 text-sm md:text-base text-gray-500 dark:text-gray-400 leading-snug mb-2">Create a profile that showcases your work, skills, and availability</p>
              <a
                href="https://designjobs.careers/carlos"
                target="_blank"
                rel="noopener noreferrer"
                (click)="$event.stopPropagation()"
                class="text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:underline focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-500 rounded block mb-[34px]"
              >View example</a>
            </div>
            <span class="ext-link" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path class="arrow-icon" d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25" />
              </svg>
            </span>
          </div>
          <div class="card-glow-wrapper">
            <span class="card-glow" aria-hidden="true"></span>
          </div>
          <div class="viewport">
            <div class="profile-card-bg">
              <div class="profile-inner">
                <div class="avatar-wrap">
                  <div class="avatar">
                    <img src="assets/images/homepage/avatar.png" alt="Ben Smith" />
                  </div>
                  <div class="verified-badge" aria-hidden="true">
                    <img src="assets/images/homepage/verified.svg" alt="" />
                  </div>
                </div>
                <div class="profile-name">Ben Smith</div>
                <div class="profile-loc">🇺🇸 New York, USA</div>
                <div class="profile-bio">Product Designer with 10+ years of experience</div>
                <div class="available-badge">Available for work</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Navigation buttons in constrained container (match Explore the Design Ecosystem) -->
    <div class="w-full max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
      <div class="carousel-nav flex gap-3 mt-0 md:mt-2">
        <button
          type="button"
          aria-label="Scroll carousel left"
          [disabled]="!toolkitCanScrollLeft"
          (click)="scrollToolkitCarousel('left')"
          class="w-11 h-11 rounded-full bg-white text-gray-900 border border-gray-200 flex items-center justify-center cursor-pointer transition-colors hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none dark:bg-gray-800 dark:border-gray-700 dark:text-white dark:hover:bg-gray-700"
        >
          <hugeicons-icon
            [icon]="icons.arrowLeft"
            [size]="20"
            color="currentColor"
            [strokeWidth]="2"
          ></hugeicons-icon>
        </button>
        <button
          type="button"
          aria-label="Scroll carousel right"
          [disabled]="!toolkitCanScrollRight"
          (click)="scrollToolkitCarousel('right')"
          class="w-11 h-11 rounded-full bg-gray-900 text-white border border-gray-900 flex items-center justify-center cursor-pointer transition-colors hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none dark:bg-gray-800 dark:border-gray-700 dark:text-white dark:hover:bg-gray-700"
        >
          <hugeicons-icon
            [icon]="icons.arrowRight"
            [size]="20"
            color="currentColor"
            [strokeWidth]="2"
          ></hugeicons-icon>
        </button>
  </div>
  </div>
  </div>
</section>

<!-- 3. Explore the Design Ecosystem -->
<section
  class="relative w-full overflow-hidden py-16 md:py-24"
>
  <!-- Section grid: vertical lines constrained to content, horizontal lines full width -->
  <div
    class="absolute inset-0 pointer-events-none z-[1]"
    aria-hidden="true"
  >
    <!-- Vertical lines container (constrained to content width) -->
    <div class="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-full max-w-7xl px-4 sm:px-6 md:px-8">
      <!-- Left vertical line -->
      <span
        class="absolute left-0 top-0 bottom-0 border-l border-gray-200 dark:border-gray-800"
      ></span>
      <!-- Right vertical line -->
      <span
        class="absolute right-0 top-0 bottom-0 border-r border-gray-200 dark:border-gray-800"
      ></span>
    </div>
    <!-- Horizontal lines: bottom only (top line removed to avoid double line where sections meet) -->
    <span
      class="absolute left-0 right-0 w-full bottom-0 border-b border-gray-200 dark:border-gray-800 mix-blend-multiply"
    ></span>
  </div>

  <div class="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
    <!-- Header -->
    <div class="mb-12 md:mb-20">
      <span class="inline-block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 px-3 py-1 bg-gray-100 dark:bg-gray-800 rounded-full">Jobs</span>
      <div class="grid grid-cols-2 max-md:grid-cols-1 gap-3 md:gap-8 items-start">
        <h2 class="text-2xl font-bold text-gray-900 dark:text-white sm:text-2xl md:text-3xl lg:text-4xl">
          Explore the Design Ecosystem
        </h2>
        <p class="text-base sm:text-lg text-gray-600 dark:text-gray-400">
          Whether you're architecting functional prototypes or building a visual legacy, your next role is categorized by discipline, not just keywords.
        </p>
      </div>
    </div>

    <!-- Two-column layout -->
    <div class="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-12">
      <!-- Left: Categories -->
      <div class="lg:col-span-5 flex flex-col justify-between">
        <!-- Mobile: horizontal scroll with right-edge gradient -->
        <div
          class="categories-scroll-wrapper relative -mx-4 px-4 lg:mx-0 lg:px-0"
        >
          <nav
            #categoriesNav
            class="categories-nav hide-scrollbar flex flex-row gap-5 flex-nowrap overflow-x-auto lg:flex-col lg:gap-5 lg:overflow-visible"
          >
            <button
              *ngFor="let category of categories"
              type="button"
              (click)="selectCategory(category.name)"
              [attr.data-category]="category.name"
              class="group flex items-center gap-2 md:gap-3 flex-shrink-0 w-auto lg:w-full bg-transparent border-none rounded-none cursor-pointer transition-colors text-left text-base lg:text-xl font-normal text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-300"
              [class.!text-gray-900]="selectedCategory === category.name"
              [class.dark:!text-white]="selectedCategory === category.name"
              [class.!font-semibold]="selectedCategory === category.name"
            >
              <span
                class="w-8 h-8 min-w-8 min-h-8 lg:w-10 lg:h-10 lg:min-w-10 lg:min-h-10 rounded-lg border flex items-center justify-center transition-colors"
                [class.border-gray-900]="selectedCategory === category.name"
                [class.text-gray-900]="selectedCategory === category.name"
                [class.dark:border-gray-500]="selectedCategory === category.name"
                [class.dark:text-white]="selectedCategory === category.name"
                [class.border-gray-300]="selectedCategory !== category.name"
                [class.dark:border-gray-800]="selectedCategory !== category.name"
                [class.text-gray-400]="selectedCategory !== category.name"
                [class.dark:text-gray-500]="selectedCategory !== category.name"
                [class.group-hover:text-gray-500]="selectedCategory !== category.name"
                [class.group-hover:dark:text-gray-300]="selectedCategory !== category.name"
              >
                <span
                  class="category-icon-mask w-4 h-4 shrink-0 lg:w-5 lg:h-5"
                  [class.category-icon-mask--animate]="
                    animatingCategory === category.name
                  "
                  [style.--icon-mask]="'url(' + category.icon + ')'"
                  aria-hidden="true"
                ></span>
              </span>
              <span>{{ category.name }}</span>
            </button>
          </nav>
          <!-- Right-edge gradient on mobile to indicate scroll -->
          <span
            class="categories-scroll-fade absolute right-0 top-0 bottom-0 w-12 lg:hidden pointer-events-none z-10 bg-gradient-to-l from-white to-transparent dark:from-gray-950"
            aria-hidden="true"
          ></span>
        </div>

        <!-- Navigation arrows (desktop only: next to categories) -->
        <div class="hidden lg:flex gap-3 mt-8">
          <button
            type="button"
            (click)="selectPreviousCategory()"
            [disabled]="categories[0].name === selectedCategory"
            class="w-11 h-11 rounded-full bg-white text-gray-900 border border-gray-200 flex items-center justify-center cursor-pointer transition-colors hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-700 dark:text-white dark:hover:bg-gray-700"
            aria-label="Previous category"
          >
            <hugeicons-icon
              [icon]="icons.arrowLeft"
              [size]="20"
              color="currentColor"
              [strokeWidth]="2"
            ></hugeicons-icon>
          </button>
          <button
            type="button"
            (click)="selectNextCategory()"
            [disabled]="categories[categories.length - 1].name === selectedCategory"
            class="w-11 h-11 rounded-full bg-gray-900 text-white border border-gray-900 flex items-center justify-center cursor-pointer transition-colors hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-700 dark:text-white dark:hover:bg-gray-700"
            aria-label="Next category"
          >
            <hugeicons-icon
              [icon]="icons.arrowRight"
              [size]="20"
              color="currentColor"
              [strokeWidth]="2"
            ></hugeicons-icon>
          </button>
        </div>
      </div>

      <!-- Right: Job cards (grey container, white cards scroll inside) -->
      <div class="lg:col-span-6 lg:col-start-7">
        <div class="relative rounded-xl px-3 md:px-5 pb-0 flex flex-col md:h-[550px] h-[350px] min-h-0 bg-[#F2F2F2] dark-gradient-bg dark:border dark:border-gray-800">
          
          <!-- Arrow button - top right of container, always visible -->
          <button
            type="button"
            (click)="viewAllJobs()"
            class="absolute -top-[10px] -right-[10px] lg:top-[-16px] lg:right-[-12px] z-10 w-8 h-8 md:w-[34px] md:h-[34px] rounded-full bg-gray-900 dark:bg-white text-white dark:text-gray-900 flex items-center justify-center cursor-pointer transition-all hover:scale-110"
            aria-label="View all jobs"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="w-3 h-3 md:w-[13px] md:h-[13px]">
              <path d="M7 17L17 7M17 7H7M17 7V17"/>
            </svg>
          </button>

          <!-- Error state -->
          <div *ngIf="jobsError && !isLoadingJobs" class="p-8 text-center">
            <p class="text-red-600 dark:text-red-400">{{ jobsError }}</p>
            <button
              type="button"
              (click)="loadJobsForCategory(selectedCategory)"
              class="mt-4 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Try Again
            </button>
          </div>

          <!-- Jobs list -->
          <div
            *ngIf="!jobsError"
            data-lenis-prevent
            class="ecosystem-jobs-scroll overflow-y-scroll overflow-x-hidden min-h-0 flex-1 flex flex-col gap-3 pr-1"
          >
            <!-- Skeleton loading (same layout as job cards: gap-3, p-4, w-12 logo) -->
            <ng-container *ngIf="isLoadingJobs">
              <div
                *ngFor="let _ of skeletonJobs; let first = first"
                class="flex items-center gap-3 p-4 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl flex-shrink-0 animate-pulse"
                [class.mt-5]="first"
              >
                <!-- Logo skeleton - same as job card logo -->
                <div class="w-12 h-12 rounded-full bg-gray-300 dark:bg-gray-700 flex-shrink-0"></div>

                <!-- Content skeleton - title + badge row, company line -->
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2 mb-0.5">
                    <div class="h-4 bg-gray-300 dark:bg-gray-700 rounded w-2/3"></div>
                    <div class="h-5 w-14 bg-gray-200 dark:bg-gray-800 rounded-md flex-shrink-0"></div>
                  </div>
                  <div class="h-4 bg-gray-200 dark:bg-gray-800 rounded w-1/3"></div>
                </div>

                <!-- Date skeleton -->
                <div class="h-4 w-12 bg-gray-200 dark:bg-gray-800 rounded flex-shrink-0"></div>
              </div>
            </ng-container>

            <!-- Real job cards -->
            <ng-container *ngIf="!isLoadingJobs && jobs.length > 0">
              <a
                *ngFor="let job of jobs; let first = first"
                [href]="'/job/' + job.id"
                target="_blank"
                rel="noopener noreferrer"
                (click)="trackHomeClick('home_ecosystem_job_clicked', { job_id: job.id, job_title: job.title, company: job.company })"
                class="flex items-center gap-3 p-3 md:p-4 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl transition-[border-color,box-shadow] duration-300 ease-out cursor-pointer flex-shrink-0 hover:border-gray-400 dark:hover:border-gray-600 hover:shadow-lg"
                [class.mt-5]="first"
              >
                <!-- Company logo - circular, neutral bg when no image (match job-list-display: object-contain) -->
                <div class="w-10 h-10 md:w-12 md:h-12 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0 overflow-hidden border border-gray-200 dark:border-gray-700">
                  <img
                    *ngIf="job.logo_img"
                    [src]="job.logo_img"
                    [alt]="job.company + ' logo'"
                    class="w-full h-full object-contain"
                  />
                  <svg
                    *ngIf="!job.logo_img"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    class="w-5 h-5 md:w-6 md:h-6 text-gray-500 dark:text-gray-400"
                  >
                    <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z"/>
                  </svg>
                </div>

                <!-- Job info - desktop: title + badge inline, company below; mobile: title, company, then badges -->
                <div class="flex-1 min-w-0 flex flex-col gap-0.5">
                  <div class="flex items-center gap-2 flex-wrap min-w-0">
                    <h3 class="text-sm text-gray-900 dark:text-white truncate">{{ job.title }}</h3>
                    <!-- Badges next to title (desktop only) -->
                    <span
                      *ngIf="job.work_type === 'remote'"
                      class="hidden md:inline-block px-2 py-0.5 text-xs font-semibold rounded flex-shrink-0 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300"
                    >Remote</span>
                    <span
                      *ngIf="job.work_type === 'hybrid'"
                      class="hidden md:inline-block px-2 py-0.5 text-xs font-semibold rounded flex-shrink-0 bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300"
                    >Hybrid</span>
                    <span
                      *ngIf="job.work_type === 'onsite'"
                      class="hidden md:inline-block px-2 py-0.5 text-xs font-semibold rounded flex-shrink-0 bg-[rgba(207,218,202,0.47)] dark:bg-[#d4efe4] text-[#2d4a3e] dark:text-[#1a3d2e]"
                    >Onsite</span>
                  </div>
                  <p class="text-sm text-gray-900 dark:text-gray-100 truncate">{{ job.company }}</p>
                  <!-- Badges below company (mobile only) -->
                  <div class="flex flex-wrap gap-1.5 hidden mt-1">
                    <span
                      *ngIf="job.work_type === 'remote'"
                      class="inline-block px-2 py-0.5 text-xs font-semibold rounded flex-shrink-0 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300"
                    >Remote</span>
                    <span
                      *ngIf="job.work_type === 'hybrid'"
                      class="inline-block px-2 py-0.5 text-xs font-semibold rounded flex-shrink-0 bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300"
                    >Hybrid</span>
                    <span
                      *ngIf="job.work_type === 'onsite'"
                      class="inline-block px-2 py-0.5 text-xs font-semibold rounded flex-shrink-0 bg-[rgba(207,218,202,0.47)] dark:bg-[#d4efe4] text-[#2d4a3e] dark:text-[#1a3d2e]"
                    >Onsite</span>
                  </div>
                </div>

                <!-- Date - right side, vertically centered -->
                <span
                  *ngIf="formatJobPostedTime(job.created_at)"
                  class="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap flex-shrink-0"
                >
                  {{ formatJobPostedTime(job.created_at) }}
                </span>
              </a>

              <!-- View all jobs CTA -->
              <button
                type="button"
                (click)="viewAllJobs('list')"
                class="btn-secondary w-full flex-shrink-0 mt-4 mb-10 md:mb-12"
              >
                View all jobs
              </button>
            </ng-container>

            <!-- Empty state -->
            <div *ngIf="!isLoadingJobs && jobs.length === 0" class="p-8 text-center">
              <p class="text-gray-500 dark:text-gray-400">No jobs found for {{ selectedCategory }}</p>
            </div>
          </div>

          <!-- Mobile: bottom gradient to indicate scrollable content -->
          <div
            class="absolute bottom-0 left-0 right-0 h-12 pointer-events-none z-10 rounded-b-xl bg-gradient-to-t from-[#F2F2F2] via-[#F2F2F2]/70 to-transparent dark:from-black dark:via-black/80"
            aria-hidden="true"
          ></div>
        </div>

        <!-- Navigation arrows (mobile only: below job cards) -->
        <div class="flex lg:hidden gap-3 mt-6 justify-start">
          <button
            type="button"
            (click)="selectPreviousCategory()"
            [disabled]="categories[0].name === selectedCategory"
            class="w-9 h-9 md:w-11 md:h-11 rounded-full bg-white text-gray-900 border border-gray-200 flex items-center justify-center cursor-pointer transition-colors hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-700 dark:text-white dark:hover:bg-gray-700"
            aria-label="Previous category"
          >
            <hugeicons-icon
              [icon]="icons.arrowLeft"
              [size]="20"
              color="currentColor"
              [strokeWidth]="2"
            ></hugeicons-icon>
          </button>
          <button
            type="button"
            (click)="selectNextCategory()"
            [disabled]="categories[categories.length - 1].name === selectedCategory"
            class="w-9 h-9 md:w-11 md:h-11 rounded-full bg-gray-900 text-white border border-gray-900 flex items-center justify-center cursor-pointer transition-colors hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-700 dark:text-white dark:hover:bg-gray-700"
            aria-label="Next category"
          >
            <hugeicons-icon
              [icon]="icons.arrowRight"
              [size]="20"
              color="currentColor"
              [strokeWidth]="2"
            ></hugeicons-icon>
          </button>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- 4. For Companies - Elite Design Talent -->
<section class="relative w-full overflow-hidden py-16 md:py-24">
  <!-- Section grid: vertical lines constrained to content -->
  <div
    class="absolute inset-0 pointer-events-none z-[1]"
    aria-hidden="true"
  >
    <div class="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-full max-w-7xl px-4 sm:px-6 md:px-8">
      <span
        class="absolute left-0 top-0 bottom-0 border-l border-gray-200 dark:border-gray-800"
      ></span>
      <span
        class="absolute right-0 top-0 bottom-0 border-r border-gray-200 dark:border-gray-800"
      ></span>
    </div>
  </div>

  <div class="relative z-10 w-full max-w-6xl mx-auto px-4 sm:px-6 md:px-8">
    <div class="companies-cta-hover group relative rounded-3xl overflow-hidden min-h-[300px] md:min-h-[330px] flex items-center justify-center max-w-5xl mx-auto bg-gray-100 dark-gradient-bg dark:border dark:border-gray-800">
      <!-- Inner clip for blur/background only -->
      <div class="absolute inset-0 rounded-3xl overflow-hidden" aria-hidden="true">
        <div class="absolute inset-0 bg-gray-100 dark-gradient-bg dark:border dark:border-gray-800"></div>
        <!-- Two blur shapes: purple (left), green (right) -->
        <div
          class="absolute top-[0px] left-[-30px] md:top-1/2 md:-translate-y-1/2 md:left-[4%] w-[140px] h-[140px] rounded-[40%] blur-[56px] opacity-65 pointer-events-none z-[1] bg-gradient-to-br from-[rgba(149,31,175,0.7)] to-[rgba(149,31,175,0.5)] dark:from-[rgba(149,31,175,0.4)] dark:to-[rgba(149,31,175,0.25)]"
          aria-hidden="true"
        ></div>
        <div
          class="absolute top-[220px] md:top-1/2 md:-translate-y-1/2 right-[4%] w-[140px] h-[140px] rounded-[40%] blur-[56px] opacity-65 pointer-events-none z-[1] bg-gradient-to-br from-[rgba(88,163,50,0.65)] to-[rgba(88,163,50,0.5)] dark:from-[rgba(88,163,50,0.35)] dark:to-[rgba(88,163,50,0.2)]"
          aria-hidden="true"
        ></div>
      </div>

      <!-- Profile images - absolute, widths as specified -->
      <div
        class="cta-photo cta-photo--1 md:top-0 md:-left-[10px] top-[-12px] left-[-7px] md:w-[171px] md:h-[171px] w-[79px] h-[79px]"
        style="--shine-delay: 0.2s; --float-delay: 0.0s"
        aria-hidden="true"
      >
        <img src="assets/images/homepage/image-1.png" alt="" class="w-full h-full" />
      </div>
      <div
        class="cta-photo cta-photo--2 left-[16px] md:left-[100px] top-[200px] md:w-[100px] md:h-[100px] w-[70px] h-[70px]"
        style="--shine-delay: 1.1s; --float-delay: 0.6s"
        aria-hidden="true"
      >
        <img src="assets/images/homepage/image-2.png" alt="" class="w-full h-full" />
      </div>
      <div
        class="cta-photo cta-photo--3 top-[10px] right-[10px] md:w-[70px] md:h-[70px] w-[50px] h-[50px]"
        style="--shine-delay: 2.0s; --float-delay: 1.2s"
        aria-hidden="true"
      >
        <img src="assets/images/homepage/image-3.png" alt="" class="w-full h-full" />
      </div>
      <div
        class="cta-photo cta-photo--4 right-[-14px] bottom-[-10px] md:w-[211px] md:h-[211px] w-[111px] h-[111px]"
        style="--shine-delay: 0.6s; --float-delay: 0.3s"
        aria-hidden="true"
      >
        <img src="assets/images/homepage/image-4.png" alt="" class="w-full h-full" />
      </div>

      <!-- Center content -->
      <div class="relative z-10 text-center px-6 py-8 mx-auto">
        <span class="inline-block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 px-3 py-1 bg-gray-200 dark:bg-gray-800 rounded-full">
          For companies
        </span>
        <h2 class="text-xl font-bold text-gray-900 dark:text-white sm:text-2xl md:text-3xl lg:text-4xl tracking-tight leading-tight mb-2 md:mb-4">
          The direct line to Elite Design Talent
        </h2>
        <p class="text-sm sm:text-lg text-gray-600 dark:text-gray-400 leading-relaxed mb-6 md:mb-8 max-w-lg mx-auto">
          From seed-stage pioneers to global leaders, top teams use Design Jobs Careers to find specialized talent.
        </p>
        <a routerLink="/post-job" class="btn-primary">
          Post a job
        </a>
      </div>
    </div>
  </div>
</section>

<!-- Never miss an opportunity - Infinite Scroll Marquee -->
<section class="relative w-full overflow-hidden py-12 md:py-16">
  <!-- Section grid: vertical lines constrained to content, horizontal line bottom only (avoids double line with section above) -->
  <div
    class="absolute inset-0 pointer-events-none z-[1]"
    aria-hidden="true"
  >
    <div class="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-full max-w-7xl px-4 sm:px-6 md:px-8">
      <span
        class="absolute left-0 top-0 bottom-0 border-l border-gray-200 dark:border-gray-800"
      ></span>
      <span
        class="absolute right-0 top-0 bottom-0 border-r border-gray-200 dark:border-gray-800"
      ></span>
    </div>
    <span
      class="absolute left-0 right-0 w-full bottom-0 border-b border-gray-200 dark:border-gray-800 mix-blend-multiply"
    ></span>
  </div>

  <div class="relative z-10">
    <div class="mb-8 text-center">
      <h2 class="text-xl font-bold text-gray-900 dark:text-white sm:text-2xl md:text-3xl">
        Never miss an opportunity
      </h2>
    </div>

    <!-- Three rows of infinite scrolling announcements -->
    <div class="relative w-full">
      <!-- Edge gradients -->
      <div
        class="absolute left-0 top-0 bottom-0 w-24 sm:w-32 pointer-events-none z-10 bg-gradient-to-r from-white to-transparent dark:from-black"
        aria-hidden="true"
      ></div>
      <div
        class="absolute right-0 top-0 bottom-0 w-24 sm:w-32 pointer-events-none z-10 bg-gradient-to-l from-white to-transparent dark:from-black"
        aria-hidden="true"
      ></div>

      <div class="flex flex-col gap-3 w-full group">
    <!-- Row 1: Scroll Left -->
    <div class="flex overflow-hidden whitespace-nowrap relative w-full h-8">
      <div class="marquee-track marquee-scroll-left flex gap-3">
        <a
          *ngFor="let item of hiringAnnouncements.slice(0, 17); let i = index"
          [routerLink]="['/job', item.id]"
          target="_blank"
          [class.ml-3]="i === 0"
          class="inline-flex items-center gap-2 h-8 py-1.5 px-3 rounded-[22px] bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shrink-0 whitespace-nowrap no-underline text-gray-900 dark:text-gray-100 text-[13px] font-medium transition-all duration-200 hover:bg-gray-200 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-md"
        >
          <img
            *ngIf="item.logo_img"
            [src]="item.logo_img"
            [alt]="item.company + ' logo'"
            referrerpolicy="no-referrer"
            (error)="onLogoError($event)"
            class="w-5 h-5 object-contain shrink-0 rounded"
          />
          <span>{{ item.company }} is hiring a {{ item.cleanTitle }}</span>
        </a>
      </div>
      <div class="marquee-track marquee-scroll-left flex gap-3" aria-hidden="true">
        <a
          *ngFor="let item of hiringAnnouncements.slice(0, 17); let i = index"
          [routerLink]="['/job', item.id]"
          target="_blank"
          [class.ml-3]="i === 0"
          class="inline-flex items-center gap-2 h-8 py-1.5 px-3 rounded-[22px] bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shrink-0 whitespace-nowrap no-underline text-gray-900 dark:text-gray-100 text-[13px] font-medium transition-all duration-200 hover:bg-gray-200 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-md"
        >
          <img
            *ngIf="item.logo_img"
            [src]="item.logo_img"
            [alt]="item.company + ' logo'"
            referrerpolicy="no-referrer"
            (error)="onLogoError($event)"
            class="w-5 h-5 object-contain shrink-0 rounded"
          />
          <span>{{ item.company }} is hiring a {{ item.cleanTitle }}</span>
        </a>
      </div>
    </div>

    <!-- Row 2: Scroll Right -->
    <div class="flex overflow-hidden whitespace-nowrap relative w-full h-8">
      <div class="marquee-track marquee-scroll-right flex gap-3">
        <a
          *ngFor="let item of hiringAnnouncements.slice(17, 34); let i = index"
          [routerLink]="['/job', item.id]"
          target="_blank"
          [class.ml-3]="i === 0"
          class="inline-flex items-center gap-2 h-8 py-1.5 px-3 rounded-[22px] bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shrink-0 whitespace-nowrap no-underline text-gray-900 dark:text-gray-100 text-[13px] font-medium transition-all duration-200 hover:bg-gray-200 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-md"
        >
          <img
            *ngIf="item.logo_img"
            [src]="item.logo_img"
            [alt]="item.company + ' logo'"
            referrerpolicy="no-referrer"
            (error)="onLogoError($event)"
            class="w-5 h-5 object-contain shrink-0 rounded"
          />
          <span>{{ item.company }} is hiring a {{ item.cleanTitle }}</span>
        </a>
      </div>
      <div class="marquee-track marquee-scroll-right flex gap-3" aria-hidden="true">
        <a
          *ngFor="let item of hiringAnnouncements.slice(17, 34); let i = index"
          [routerLink]="['/job', item.id]"
          target="_blank"
          [class.ml-3]="i === 0"
          class="inline-flex items-center gap-2 h-8 py-1.5 px-3 rounded-[22px] bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shrink-0 whitespace-nowrap no-underline text-gray-900 dark:text-gray-100 text-[13px] font-medium transition-all duration-200 hover:bg-gray-200 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-md"
        >
          <img
            *ngIf="item.logo_img"
            [src]="item.logo_img"
            [alt]="item.company + ' logo'"
            referrerpolicy="no-referrer"
            (error)="onLogoError($event)"
            class="w-5 h-5 object-contain shrink-0 rounded"
          />
          <span>{{ item.company }} is hiring a {{ item.cleanTitle }}</span>
        </a>
      </div>
    </div>

    <!-- Row 3: Scroll Left -->
    <div class="flex overflow-hidden whitespace-nowrap relative w-full h-8">
      <div class="marquee-track marquee-scroll-left flex gap-3">
        <a
          *ngFor="let item of hiringAnnouncements.slice(34, 50); let i = index"
          [routerLink]="['/job', item.id]"
          target="_blank"
          [class.ml-3]="i === 0"
          class="inline-flex items-center gap-2 h-8 py-1.5 px-3 rounded-[22px] bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shrink-0 whitespace-nowrap no-underline text-gray-900 dark:text-gray-100 text-[13px] font-medium transition-all duration-200 hover:bg-gray-200 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-md"
        >
          <img
            *ngIf="item.logo_img"
            [src]="item.logo_img"
            [alt]="item.company + ' logo'"
            referrerpolicy="no-referrer"
            (error)="onLogoError($event)"
            class="w-5 h-5 object-contain shrink-0 rounded"
          />
          <span>{{ item.company }} is hiring a {{ item.cleanTitle }}</span>
        </a>
      </div>
      <div class="marquee-track marquee-scroll-left flex gap-3" aria-hidden="true">
        <a
          *ngFor="let item of hiringAnnouncements.slice(34, 50); let i = index"
          [routerLink]="['/job', item.id]"
          target="_blank"
          [class.ml-3]="i === 0"
          class="inline-flex items-center gap-2 h-8 py-1.5 px-3 rounded-[22px] bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shrink-0 whitespace-nowrap no-underline text-gray-900 dark:text-gray-100 text-[13px] font-medium transition-all duration-200 hover:bg-gray-200 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-md"
        >
          <img
            *ngIf="item.logo_img"
            [src]="item.logo_img"
            [alt]="item.company + ' logo'"
            referrerpolicy="no-referrer"
            (error)="onLogoError($event)"
            class="w-5 h-5 object-contain shrink-0 rounded"
          />
          <span>{{ item.company }} is hiring a {{ item.cleanTitle }}</span>
        </a>
      </div>
    </div>
  </div>
  </div>
  </div>
</section>

<!-- Founder's Note: full text in SSR HTML for Google; collapse only in browser -->
<section id="founders-note" class="relative w-full overflow-hidden py-16 md:py-24">
  <!-- Section grid: vertical lines constrained to content, horizontal line bottom only -->
  <div
    class="absolute inset-0 pointer-events-none z-[1]"
    aria-hidden="true"
  >
    <div class="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-full max-w-7xl px-4 sm:px-6 md:px-8">
      <span
        class="absolute left-0 top-0 bottom-0 border-l border-gray-200 dark:border-gray-800"
      ></span>
      <span
        class="absolute right-0 top-0 bottom-0 border-r border-gray-200 dark:border-gray-800"
      ></span>
    </div>
    <span
      class="absolute left-0 right-0 w-full bottom-0 border-b border-gray-200 dark:border-gray-800 mix-blend-multiply"
    ></span>
  </div>

  <div class="relative z-10 w-full max-w-3xl mx-auto px-4 sm:px-6 md:px-8">
    <div class="text-center mb-4">
      <h2 class="sr-only">Founder's note</h2>
      <span class="inline-block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 px-3 py-1 bg-gray-100 dark:bg-gray-800 rounded-full" aria-hidden="true">
        FOUNDERS NOTE
      </span>
    </div>

    <div class="relative">
      <!-- First paragraph: collapsed height only in browser when not expanded -->
      <div 
        class="relative overflow-hidden transition-all duration-500 ease-in-out"
        [class.max-h-[1000px]]="foundersNoteExpanded || !isBrowser"
        [class.max-h-[200px]]="isBrowser && !foundersNoteExpanded"
        [class.delay-[600ms]]="isBrowser && !foundersNoteExpanded"
      >
        <h3 class="text-lg md:text-3xl font-semibold md:leading-snug text-gray-900 dark:text-white mb-5">
          I felt the need to create Designjobs to provide new designers with the tools to succeed on landing a job in 2026, where AI is the word of the day. The job market has changed, and I wanted to build something that actually helps people navigate it.
        </h3>
        
        <!-- Gradient overlay: only when in browser and collapsed -->
        <div 
          class="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white dark:from-gray-950 to-transparent pointer-events-none transition-opacity duration-500 ease-in-out"
          [class.opacity-0]="foundersNoteExpanded || !isBrowser"
          [class.opacity-100]="isBrowser && !foundersNoteExpanded"
          [class.delay-[600ms]]="isBrowser && !foundersNoteExpanded"
        ></div>
      </div>
      
      <!-- Expandable content: visible in SSR; collapsed only in browser when not expanded -->
      <div 
        class="transition-all duration-500 ease-in-out overflow-hidden"
        [class.max-h-[1000px]]="foundersNoteExpanded || !isBrowser"
        [class.max-h-0]="isBrowser && !foundersNoteExpanded"
        [class.opacity-100]="foundersNoteExpanded || !isBrowser"
        [class.opacity-0]="isBrowser && !foundersNoteExpanded"
      >
        <h3 class="text-lg md:text-3xl font-semibold md:leading-snug text-gray-900 dark:text-white mb-5">
          I used the magic wand for the logo because I feel designers always bring their magic to the work they do. That human touch, that creative intuition, is something unique that I don't think AI can replace.
        </h3>
        <h3 class="text-lg md:text-3xl font-semibold md:leading-snug text-gray-900 dark:text-white mb-5">
          If you're just starting out or trying to make a career switch, I hope this helps. And if you have ideas on how to make it better, I'd love to hear from you. I'm building this as I go, and there's a lot more I want to add. This is just the beginning."
        </h3>
      </div>
    </div>

    <button
      type="button"
      (click)="toggleFoundersNote()"
      class="text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors underline cursor-pointer mt-4 mb-12 block"
    >
      {{ foundersNoteExpanded ? 'Show less' : 'Show more' }}
    </button>

    <!-- Author info -->
    <div class="flex items-center gap-2 md:gap-4">
      <!-- Profile image -->
      <img 
        src="assets/images/homepage/profile-image.png"
        alt="Carlos Palma"
        class="w-10 h-10 md:w-12 md:h-12 flex-shrink-0 object-cover"
      />
      
      <div>
        <a
          href="https://linkedin.com/in/cpalma1993"
          target="_blank"
          rel="noopener noreferrer"
          class="block text-base font-semibold text-gray-900 dark:text-white hover:underline transition-all duration-200"
        >
          Carlos Palma
        </a>
        <p class="text-xs md:text-sm text-gray-600 dark:text-gray-400">
          Founder of Designjobs.careers
        </p>
      </div>
      
      <!-- Magic wand icon -->
      <div class="ml-auto">
        <svg width="24" height="24" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15.9965 18L11.7465 13.75M18.8445 3.125V1M24.4252 5.3359L25.9278 3.8333M24.4252 16.5833L25.9278 18.0859M13.1778 5.3359L11.6752 3.8333M26.6362 10.9167H28.7612M6.2659 27.7306L19.3521 14.6444C19.9131 14.0834 20.1936 13.8029 20.2987 13.4794C20.3911 13.1949 20.3911 12.8884 20.2987 12.6039C20.1936 12.2804 19.9131 11.9999 19.3521 11.4389L18.3076 10.3944C17.7466 9.8334 17.4661 9.5529 17.1426 9.4478C16.8581 9.3554 16.5516 9.3554 16.2671 9.4478C15.9436 9.5529 15.6631 9.8334 15.1021 10.3944L2.0159 23.4806C1.4549 24.0416 1.1744 24.3221 1.0693 24.6456C0.9769 24.9301 0.9769 25.2366 1.0693 25.5211C1.1744 25.8446 1.4549 26.1251 2.0159 26.6861L3.0604 27.7306C3.6214 28.2916 3.9019 28.5721 4.2254 28.6772C4.5099 28.7696 4.8164 28.7696 5.1009 28.6772C5.4244 28.5721 5.7049 28.2916 6.2659 27.7306Z" stroke="url(#paint0_linear_525_12652)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <defs>
            <linearGradient id="paint0_linear_525_12652" x1="1" y1="14.8733" x2="28.7612" y2="14.8733" gradientUnits="userSpaceOnUse">
              <stop stop-color="#8300FF"/>
              <stop offset="0.471154" stop-color="#B70AC4"/>
              <stop offset="1" stop-color="#F21582"/>
            </linearGradient>
          </defs>
        </svg>
      </div>
    </div>
  </div>
</section>

<!-- FAQ Section -->
<section
  class="relative w-full overflow-hidden py-16 md:py-24"
>
  <!-- Section grid: vertical lines constrained to content, horizontal lines full width -->
  <div
    class="absolute inset-0 pointer-events-none z-[1]"
    aria-hidden="true"
  >
    <!-- Vertical lines container (constrained to content width) -->
    <div class="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-full max-w-7xl px-4 sm:px-6 md:px-8">
      <!-- Left vertical line -->
      <span
        class="absolute left-0 top-0 bottom-0 border-l border-gray-200 dark:border-gray-800"
      ></span>
      <!-- Right vertical line -->
      <span
        class="absolute right-0 top-0 bottom-0 border-r border-gray-200 dark:border-gray-800"
      ></span>
    </div>
    <!-- Horizontal lines: bottom only -->
    <span
      class="absolute bottom-0 left-0 right-0 border-b border-gray-200 dark:border-gray-800"
    ></span>
  </div>

  <!-- Content -->
  <div class="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
    <!-- Header -->
    <div class="mb-12 md:mb-20">
      <div class="grid grid-cols-2 max-md:grid-cols-1 gap-3 md:gap-8 items-start">
        <h2 class="text-2xl font-bold text-gray-900 dark:text-white sm:text-2xl md:text-3xl lg:text-4xl">
          Frequently asked questions
        </h2>
        <p class="text-base sm:text-lg text-gray-600 dark:text-gray-400">
          Everything you need to know about the platform, the Job Matcher, and how we curate the world’s best design roles.        </p>
      </div>
    </div>

    <!-- Two-column layout: on mobile FAQ first, then Contact; on lg Contact left, FAQ right -->
    <div class="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-12">
      <!-- Left on lg: Contact section (order-2 on mobile so it appears below FAQ) -->
      <div class="order-2 lg:order-1 lg:col-span-5 flex flex-col justify-end">
        <div class="bg-[#F2F2F2] dark:bg-gray-800 rounded-xl p-8 max-w-[260px]">
          <h3 class="text-lg md:text-xl font-bold text-gray-900 dark:text-white mb-4">
            Still got questions?
          </h3>
          <a
            href="http://localhost:4200/contact"
            target="_blank"
            rel="noopener noreferrer"
            class="btn-primary inline-block"
          >
            Contact us
          </a>
        </div>
      </div>

      <!-- Right on lg: FAQ Cards (order-1 on mobile so it appears first) -->
      <div class="order-1 lg:order-2 lg:col-span-6 lg:col-start-7">
        <div class="relative rounded-xl px-3 md:px-5 pb-0 flex flex-col h-[350px] md:h-[520px] min-h-0 bg-[#F2F2F2] dark-gradient-bg dark:border dark:border-gray-800">
          <div
            data-lenis-prevent
            class="overflow-y-scroll overflow-x-hidden min-h-0 flex-1 flex flex-col gap-3 pr-1"
          >
            <div 
              *ngFor="let faq of faqs; let i = index"
              class="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl transition-[border-color,box-shadow] duration-300 ease-out flex-shrink-0 hover:border-gray-400 dark:hover:border-gray-600 hover:shadow-lg"
              [class.mt-5]="i === 0"
              [class.mb-8]="i === faqs.length - 1"
              [class.md:mb-0]="i === faqs.length - 1"
            >
              <button 
                class="w-full p-4 text-left focus:outline-none rounded-2xl"
                (click)="toggleFaq(i)"
                [attr.aria-expanded]="expandedFaqs.has(i)"
                [attr.aria-controls]="'faq-answer-' + i"
              >
                <div class="flex items-start justify-between gap-4 items-center">
                  <h4 class="text-sm md:text-base font-medium font-sans text-gray-900 dark:text-white">
                    {{ faq.question }}
                  </h4>
                  <span 
                    class="flex-shrink-0 w-[22px] h-[22px] bg-gray-900 dark:bg- text-white dark:text-gray-900 rounded-full flex items-center justify-center"
                  >
                    <hugeicons-icon
                      [icon]="expandedFaqs.has(i) ? icons.minus : icons.plus"
                      [size]="20"
                      color="currentColor"
                      [strokeWidth]="2"
                    ></hugeicons-icon>
                  </span>
                </div>
              </button>
              <div 
                [id]="'faq-answer-' + i"
                class="overflow-hidden transition-all duration-300 ease-in-out"
                [class.max-h-[500px]]="expandedFaqs.has(i)"
                [class.max-h-0]="!expandedFaqs.has(i)"
                [class.opacity-100]="expandedFaqs.has(i)"
                [class.opacity-0]="!expandedFaqs.has(i)"
                role="region"
                [attr.aria-labelledby]="'faq-question-' + i"
              >
                <div
                  class="px-4 pb-4 text-sm font-sans text-gray-600 dark:text-gray-400 leading-relaxed faq-answer"
                  [innerHTML]="faq.answer"
                ></div>
              </div>
            </div>
          </div>

          <!-- Mobile: bottom gradient to indicate scrollable content -->
          <div
            class="absolute bottom-0 left-0 right-0 h-12 pointer-events-none z-10 rounded-b-xl bg-gradient-to-t from-[#F2F2F2] via-[#F2F2F2]/70 to-transparent dark:from-black dark:via-black/80"
            aria-hidden="true"
          ></div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- Insights Section -->
<section
  class="relative w-full overflow-hidden py-16 md:py-24"
>
  <!-- Section grid: vertical lines constrained to content, horizontal lines full width -->
  <div
    class="absolute inset-0 pointer-events-none z-[1]"
    aria-hidden="true"
  >
    <!-- Vertical lines container (constrained to content width) -->
    <div class="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-full max-w-7xl px-4 sm:px-6 md:px-8">
      <!-- Left vertical line -->
      <span
        class="absolute left-0 top-0 bottom-0 border-l border-gray-200 dark:border-gray-800"
      ></span>
      <!-- Right vertical line -->
      <span
        class="absolute right-0 top-0 bottom-0 border-r border-gray-200 dark:border-gray-800"
      ></span>
    </div>
    <!-- Horizontal lines: bottom only -->
    <span
      class="absolute bottom-0 left-0 right-0 border-b border-gray-200 dark:border-gray-800"
    ></span>
  </div>

  <div class="relative z-10 insights-section">
    <!-- Header content in constrained container -->
    <div class="w-full max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
      <span class="inline-block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 px-3 py-1 bg-gray-100 dark:bg-gray-800 rounded-full">CONTENT</span>
      <div class="grid grid-cols-2 max-md:grid-cols-1 gap-3 md:gap-8 items-start mb-12 md:mb-20">
        <h2 class="text-2xl font-bold text-gray-900 dark:text-white sm:text-2xl md:text-3xl lg:text-4xl tracking-tight leading-tight">
          Insights
        </h2>
        <p class="text-base sm:text-lg text-gray-600 dark:text-gray-400">
          Stay informed on the trends shaping the 2026 design landscape.
        </p>
      </div>
    </div>

    <!-- Cards scroll area: starts at container edge, overflows to viewport edge -->
    <div
      #insightsCardsScroll
      class="insights-cards-scroll"
      role="region"
      aria-label="Insights blog cards"
    >
      <!-- Loading state -->
      <div 
        *ngIf="isBlogsLoading"
        class="insights-cards-grid"
      >
        <div
          *ngFor="let i of [1, 2, 3, 4, 5, 6, 7, 8]"
          class="rounded-2xl overflow-hidden shadow-md animate-pulse flex-shrink-0 w-[320px] h-[280px]"
        >
          <div class="bg-gray-200 dark:bg-gray-700 w-full h-full"></div>
        </div>
      </div>

      <!-- Real blog cards -->
      <div 
        *ngIf="!isBlogsLoading && blogPosts.length > 0"
        #insightsCardsGrid
        class="insights-cards-grid"
      >
          <!-- Blog Cards -->
          <a
            *ngFor="let post of blogPosts"
            [href]="'/blog/' + (post.slug || post.id)"
            target="_blank"
            rel="noopener noreferrer"
            class="relative bg-gray-900 rounded-2xl overflow-hidden shadow-md cursor-pointer group hover:shadow-lg transition-shadow flex-shrink-0 w-[320px] h-[280px]"
          >
            <!-- Article Image as Background -->
            <img
              *ngIf="post.image"
              [src]="post.image"
              [alt]="post.title"
              class="absolute inset-0 w-full h-full object-cover transition-transform duration-300 ease-in-out group-hover:scale-105"
            />

            <!-- Placeholder for missing image -->
            <div
              *ngIf="!post.image"
              class="absolute inset-0 bg-gray-800 w-full h-full flex items-center justify-center"
            >
              <svg
                class="w-12 h-12 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                ></path>
              </svg>
            </div>

            <!-- Overlay Gradient -->
            <div class="absolute inset-0 bg-gradient-to-t from-black/70 via-black/50 to-transparent"></div>

            <!-- Article Content Overlay -->
            <div class="relative z-10 p-6 h-full flex flex-col justify-end text-white">
              <!-- Title -->
              <h3 class="text-xl font-bold mb-2 line-clamp-3">
                {{ post.title }}
              </h3>

              <!-- Meta -->
              <div class="text-sm text-gray-200" *ngIf="post.read_time">
                {{ post.read_time }}
              </div>
            </div>
          </a>
        </div>
    </div>

    <!-- Navigation arrows and View all button (in constrained container) -->
    <div class="w-full max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
      <div class="flex items-center justify-between mt-6">
        <div class="flex gap-3">
          <button
            type="button"
            (click)="scrollInsights('left')"
            [disabled]="!insightsCanScrollLeft"
            class="w-9 h-9 md:w-11 md:h-11 rounded-full bg-white text-gray-900 border border-gray-200 flex items-center justify-center cursor-pointer transition-colors hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none dark:bg-gray-800 dark:border-gray-700 dark:text-white dark:hover:bg-gray-700"
            aria-label="Previous insight"
          >
            <hugeicons-icon
              [icon]="icons.arrowLeft"
              [size]="20"
              color="currentColor"
              [strokeWidth]="2"
            ></hugeicons-icon>
          </button>
          <button
            type="button"
            (click)="scrollInsights('right')"
            [disabled]="!insightsCanScrollRight"
            class="w-9 h-9 md:w-11 md:h-11 rounded-full bg-gray-900 text-white border border-gray-900 flex items-center justify-center cursor-pointer transition-colors hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none dark:bg-gray-800 dark:border-gray-700 dark:text-white dark:hover:bg-gray-700"
            aria-label="Next insight"
          >
            <hugeicons-icon
              [icon]="icons.arrowRight"
              [size]="20"
              color="currentColor"
              [strokeWidth]="2"
            ></hugeicons-icon>
          </button>
        </div>

        <a
          href="/blog"
          target="_blank"
          rel="noopener noreferrer"
          class="btn-secondary"
        >
          View all
        </a>
      </div>
    </div>
  </div>
</section>

<!-- CTA blocks: Find your next job / Hire your next candidate (top line omitted so it doesn't double with Insights above) -->
<section
  class="relative w-full overflow-hidden py-16 md:py-24"
>
  <!-- Section grid: vertical lines constrained to content (no bottom line on last section) -->
  <div
    class="absolute inset-0 pointer-events-none z-[1]"
    aria-hidden="true"
  >
    <div class="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-full max-w-7xl px-4 sm:px-6 md:px-8">
      <span
        class="absolute left-0 top-0 bottom-0 border-l border-gray-200 dark:border-gray-800"
      ></span>
      <span
        class="absolute right-0 top-0 bottom-0 border-r border-gray-200 dark:border-gray-800"
      ></span>
    </div>
  </div>

  <div class="relative z-10 w-full max-w-6xl mx-auto px-4 sm:px-6 md:px-8">
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
      <!-- Find your next job -->
      <div
        class="rounded-2xl bg-[#F5F3F1] dark-gradient-bg dark:border dark:border-gray-800 p-6 md:p-8 flex flex-col"
      >
        <h2 class="text-lg md:text-xl text-gray-900 dark:text-white mb-6">
          <span class="font-bold">Find your next job.</span>
          <span class="font-normal text-gray-600 dark:text-gray-400"> Browse 4,500+ design roles at companies like Figma, Stripe, and Spotify. Filter by role, location, and remote-friendly options.</span>
        </h2>
        <div class="flex flex-col sm:flex-row flex-wrap gap-3 items-stretch sm:items-center">
          <a
            href="/jobs"
            target="_blank"
            rel="noopener noreferrer"
            class="btn-primary w-full sm:w-auto text-center"
          >
            View jobs
          </a>
          <a
            routerLink="/ai-job-matcher"
            class="btn-secondary w-full sm:w-auto text-center"
          >
            Try our Job Matcher
          </a>
        </div>
      </div>

      <!-- Hire your next candidate -->
      <div
        class="rounded-2xl bg-[#F5F3F1] dark-gradient-bg dark:border dark:border-gray-800 p-6 md:p-8 flex flex-col"
      >
        <h2 class="text-lg md:text-xl text-gray-900 dark:text-white mb-6">
          <span class="font-bold">Hire your next candidate.</span>
          <span class="font-normal text-gray-600 dark:text-gray-400"> Reach designers who are actively looking. Skip the noise of generalist job boards and connect with high-intent talent.</span>
        </h2>
        <a
          href="/post-job"
          target="_blank"
          rel="noopener noreferrer"
          class="btn-primary w-full sm:w-auto sm:self-start text-center mt-auto"
        >
          Post a job
        </a>
      </div>
    </div>
  </div>
</section>

<!-- Resume analyser feature modal -->
<app-modal
  [isOpen]="showResumeAnalyserModal"
  (isOpenChange)="onResumeAnalyserModalChange($event)"
  size="lg"
>
  <!-- Full-width video with bottom gradient (points up, blends into modal bg) -->
  <div class="-mx-5 -mt-12 mb-6 sm:-mx-8 sm:-mt-8 sm:mb-8 relative">
    <video
      #resumeAnalyserVideo
      src="assets/images/resume-analyser.webm"
      class="w-full h-auto object-cover block"
      preload="auto"
      loop
      muted
      playsInline
      aria-label="Resume analyser demo"
      (canplay)="onResumeVideoCanPlay($event)"
    ></video>
    <!-- Gradient at bottom pointing up: matches modal bg for light/dark -->
    <div
      class="absolute bottom-0 left-0 right-0 h-24 sm:h-28 pointer-events-none bg-gradient-to-t from-gray-100 to-transparent dark:from-black dark:to-transparent"
      aria-hidden="true"
    ></div>
  </div>
  <h2 class="text-xl font-bold text-gray-900 dark:text-white mb-3">
    Resume analyser
  </h2>
  <p class="text-base text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
    Get instant feedback on how your resume performs against ATS systems and
    AI screening. Upload your resume to see a detailed report with scores,
    suggestions, and tailored tips so you can improve your application before
    you apply.
  </p>
  <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed mb-6">
    Before you apply to any role, run your resume through our analyser to make
    sure it makes it past automated filters and highlights your strengths.
  </p>
  <div class="flex flex-wrap gap-3">
    <button
      type="button"
      (click)="openExampleReportModal()"
      class="btn-primary w-full sm:w-auto"
    >
      View example report
    </button>
    <a
      routerLink="/jobs"
      class="btn-secondary w-full sm:w-auto"
      (click)="onResumeAnalyserModalChange(false)"
    >
      View jobs
    </a>
  </div>
</app-modal>

<app-resume-results-modal
  [isOpen]="showExampleReportModal"
  [result]="exampleResumeReport"
  [job]="exampleJob"
  (close)="closeExampleReportModal()"
></app-resume-results-modal>
